import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, today } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";
import { AttachmentButton, AttachmentsDialog } from "@/components/Attachments";

const blank = {
  date: today(), payee: "", account_id: "", payment_account_id: "", amount: 0,
  vat_rate: 0, payment_method: "cash", reference: "", notes: "",
};

export default function Expenses() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const [attachFor, setAttachFor] = useState(null);
  const [counts, setCounts] = useState({});

  const loadCounts = () => api.get("/attachments/counts", { params: { entity_type: "expense" } }).then(({ data }) => setCounts(data));
  const load = () => { loadCounts(); return api.get("/expenses").then(({ data }) => setRows(data)); };
  useEffect(() => {
    load();
    api.get("/accounts").then(({ data }) => setAccounts(data));
  }, []);

  const expenseAccounts = accounts.filter((a) => a.type === "expense");
  const cashAccounts = accounts.filter((a) => ["1000", "1010", "1020"].includes(a.code));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), vat_rate: Number(form.vat_rate) });
      toast.success("Expense recorded");
      setOpen(false); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle="Day-to-day spending paid by cash, bank or mobile money."
        action={canWrite && (
          <button data-testid="add-expense-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New expense
          </button>
        )} />

      <Panel title={`${rows.length} expenses`}>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Ref</th><th className="px-5 py-2.5">Date</th>
                  <th className="px-5 py-2.5">Payee</th><th className="px-5 py-2.5">Category</th>
                  <th className="px-5 py-2.5">Paid from</th><th className="px-5 py-2.5 text-right">Amount</th>
                  <th className="px-5 py-2.5 text-right">VAT</th><th className="px-5 py-2.5 text-right">Total</th>
                  <th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-semibold text-slate-900">{r.number}</td>
                    <td className="px-5 py-3 text-slate-600">{shortDate(r.date)}</td>
                    <td className="px-5 py-3 text-slate-700">{r.payee || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{r.account_name}</td>
                    <td className="px-5 py-3 text-slate-600">{r.payment_account_name}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.amount)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-500">{money(r.vat_amount)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{money(r.total)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <AttachmentButton testid="expense-attachments-button" count={counts[r.id]} onClick={() => setAttachFor(r)} />
                        {canWrite && (
                          <button data-testid="delete-expense-button" onClick={() => remove(r.id)}
                            className="text-slate-400 hover:text-rose-600" aria-label="Delete expense"><Trash2 size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message="No expenses recorded yet." />}
      </Panel>

      {attachFor && (
        <AttachmentsDialog entityType="expense" entityId={attachFor.id} title={attachFor.number}
          onClose={() => setAttachFor(null)} onChanged={loadCounts} />
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-md" data-testid="expense-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">New expense</h3>
              <button data-testid="close-expense-dialog" onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4 p-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Date</label>
                <input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Payee</label>
                <input data-testid="expense-payee-input" value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Expense category</label>
                <select data-testid="expense-account-select" required value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="">Select category…</option>
                  {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Paid from</label>
                <select data-testid="expense-payment-account-select" required value={form.payment_account_id} onChange={(e) => setForm({ ...form, payment_account_id: e.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="">Select account…</option>
                  {cashAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Amount (UGX)</label>
                <input data-testid="expense-amount-input" type="number" step="any" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">VAT</label>
                <select data-testid="expense-vat-select" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value={0}>No VAT</option><option value={18}>18% claimable input VAT</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Payment method</label>
                <select data-testid="expense-method-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  <option value="cash">Cash</option><option value="bank">Bank transfer</option>
                  <option value="mobile_money">Mobile money</option><option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Reference</label>
                <input data-testid="expense-reference-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <button data-testid="save-expense-button" className="col-span-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                Save &amp; post to ledger
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
